// lib/reflink.js
// Links de marcas: un código en la URL (/libros?ref=yamaha) para saber qué trajo un link
// que Brian le pasó a una marca o a otra cuenta de Instagram.
//
// El link se arma en el admin (Analytics > Links de marcas) y ahí mismo se ve el
// resultado, con datos nuestros y no los que declaran Meta o GA4:
//   visita   entró con el link              captureRef(), en cada pageload
//   carrito  tocó Comprar en una landing    trackAddToCart / trackAddToCartPack
//   pago     tocó Pagar en el checkout       trackInitiateCheckout de pay2
//   compra   la venta                       getRef() viaja en ad.ref -> fb_ads_params.ref_code
//
// Cada navegador lleva un id al azar (`v`) y el back guarda UNA fila por (ref, v, evento),
// así que el admin cuenta PERSONAS y no clicks: el que recarga la landing diez veces es uno.
//
// Mismo esquema que adparams.js y watoken.js: cookie propia más espejo en localStorage,
// porque ITP capa a 7 días las cookies escritas por JS. Vale 30 días, como el token de
// WhatsApp: pasado eso la venta ya no es del link.
//
// Gana el ÚLTIMO click, como en el resto de la atribución de la casa:
// - otro ?ref= pisa al anterior;
// - entrar por un AVISO pago lo borra (utm_medium=paid de Meta, o un gclid/gbraid/wbraid de
//   Google). Si no, la venta que trajo el aviso quedaba también en la cuenta de la marca, y
//   Links de marcas y Meta Reparto se adjudicaban la misma compra.
//   El fbclid solo NO cuenta como aviso: Instagram se lo cuelga también a los links
//   orgánicos, incluido el de la historia de la marca.

import { getCookie } from "@/utils/cookies";
import { leerPais } from "@/lib/geo/pais";

const KEY = "bcm_ref";
const MAX_AGE_DAYS = 30;
const RUTA_REPORTE = "/api/ref-evento";
const CLAVE_SESION = "bcm_ref_anotados";

/**
 * El formato que acepta el back y que entra en la columna. Se valida también al LEER,
 * porque la cookie es del cliente y puede venir editada a mano.
 */
const REF_RE = /^[a-z0-9][a-z0-9_-]{0,39}$/;
const VISITANTE_RE = /^[A-Za-z0-9-]{8,40}$/;

/**
 * El código tal como se guarda: "Yamaha Arg" -> "yamaha-arg", "@peña.music" -> "pena-music".
 * Se normaliza en vez de rechazar porque un link tocado a mano (una mayúscula, un punto)
 * se perdería en silencio. El armador del admin aplica la MISMA regla
 * (admin-front/src/components/links-marcas/codigo.ts), así que el link que se comparte y la
 * fila que se cuenta coinciden siempre. Devuelve null si no queda nada usable.
 */
export function normalizarRef(valor) {
  if (typeof valor !== "string") return null;
  const s = valor
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^[-_]+/, "")
    .slice(0, 40)
    .replace(/[-_]+$/, "");
  return REF_RE.test(s) ? s : null;
}

function persistir(obj) {
  const raw = JSON.stringify(obj);
  const maxAge = MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${KEY}=${encodeURIComponent(raw)}; max-age=${maxAge}; path=/; SameSite=Lax`;
  try {
    window.localStorage.setItem(KEY, raw);
  } catch {
    /* localStorage puede estar bloqueado (modo privado) */
  }
}

function borrar() {
  document.cookie = `${KEY}=; max-age=0; path=/; SameSite=Lax`;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Lo guardado, o null. Con `vigente: false` devuelve también lo vencido: sirve para
 * reusar el id del navegador, que no tiene por qué cambiar cuando cambia el link.
 */
function leer({ vigente = true } = {}) {
  if (typeof window === "undefined") return null;

  const fuentes = [];
  const cookie = getCookie(KEY);
  if (cookie) fuentes.push(cookie);
  try {
    const ls = window.localStorage?.getItem(KEY);
    if (ls) fuentes.push(ls);
  } catch {
    /* ignore */
  }

  for (const raw of fuentes) {
    try {
      const obj = JSON.parse(raw);
      if (!obj || typeof obj !== "object") continue;
      if (!REF_RE.test(String(obj.ref ?? "")) || !VISITANTE_RE.test(String(obj.v ?? ""))) continue;
      if (!Number.isFinite(obj.t) || obj.t <= 0) continue;
      if (vigente && Date.now() - obj.t > MAX_AGE_DAYS * 864e5) continue;
      return obj;
    } catch {
      /* valor corrupto: se ignora y se prueba la otra fuente */
    }
  }
  return null;
}

function nuevoVisitante() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* contexto no seguro: sigue el fallback */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** El click de un aviso pago. El fbclid suelto no: ver el encabezado. */
function vieneDeUnAviso(params) {
  return (
    String(params.get("utm_medium") || "").toLowerCase() === "paid" ||
    params.has("gclid") ||
    params.has("gbraid") ||
    params.has("wbraid")
  );
}

// Una vez por (ref, evento) y por sesión de pestaña: el back deduplica igual por persona,
// esto solo evita un beacon por cada recarga o por cada "+" del checkout.
const anotados = new Set();

function esNuevo(clave) {
  if (anotados.has(clave)) return false;
  anotados.add(clave);

  try {
    const previos = String(window.sessionStorage.getItem(CLAVE_SESION) || "")
      .split("|")
      .filter(Boolean);
    if (previos.includes(clave)) return false;
    previos.push(clave);
    window.sessionStorage.setItem(CLAVE_SESION, previos.slice(-12).join("|"));
  } catch {
    /* sessionStorage bloqueado: alcanza con el Set */
  }
  return true;
}

// Sincrónico y no en un setTimeout como el de pais.js: el carrito y el pago se anotan justo
// antes de saltar al checkout o a Stripe, y un timer pendiente se muere con la página. El
// sendBeacon, en cambio, lo termina de mandar el navegador aunque la pestaña ya se haya ido.
function enviar(evento, obj) {
  const campos = {
    ref: obj.ref,
    v: obj.v,
    evento,
    ruta: String(window.location.pathname || "").slice(0, 300),
  };
  if (evento === "visita") {
    // El país solo si se sabe de verdad: el fallback es siempre RW y no dice nada.
    const { pais, fuente } = leerPais();
    if ((fuente === "cc" || fuente === "geo") && pais) campos.pais = pais;
  }
  const cuerpo = new URLSearchParams(campos).toString();

  // text/plain: no dispara preflight de CORS y sobrevive a que la pestaña se cierre.
  if (typeof navigator !== "undefined" && navigator.sendBeacon) {
    navigator.sendBeacon(RUTA_REPORTE, new Blob([cuerpo], { type: "text/plain;charset=UTF-8" }));
    return;
  }
  fetch(RUTA_REPORTE, {
    method: "POST",
    body: cuerpo,
    headers: { "Content-Type": "text/plain;charset=UTF-8" },
    keepalive: true,
  }).catch(() => {});
}

function anotar(evento, obj) {
  if (!esNuevo(`${obj.ref}:${evento}`)) return;
  enviar(evento, obj);
}

/** Solo para tests: el Set vive lo que vive el módulo. */
export function _resetAnotados() {
  anotados.clear();
}

/**
 * Lee ?ref= de la URL actual, lo persiste y anota la visita.
 * Llamar una vez por pageload (GoogleAdsTag, el mismo punto que utm, fbclid y gclid).
 * Navegar el sitio sin ?ref= no toca lo guardado; solo lo borra el click de un aviso.
 */
export function captureRef() {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);

    if (params.has("ref")) {
      const ref = normalizarRef(params.get("ref"));
      // ?ref= vacío o basura: no se borra lo que había, como un utm vacío en adparams.js.
      if (!ref) return;
      const previo = leer({ vigente: false });
      const obj = { ref, t: Date.now(), v: previo?.v || nuevoVisitante() };
      persistir(obj);
      anotar("visita", obj);
      return;
    }

    if (vieneDeUnAviso(params) && leer({ vigente: false })) borrar();
  } catch {
    /* nunca romper la página por analytics */
  }
}

/**
 * Anota un paso del embudo ("carrito" | "pago") si la persona llegó por un link de marca.
 * Sin link vigente no hace nada, y nunca tira: corre adentro de los botones de compra.
 */
export function anotarRef(evento) {
  if (typeof window === "undefined") return;
  try {
    const obj = leer();
    if (obj) anotar(evento, obj);
  } catch {
    /* nunca romper la compra por analytics */
  }
}

/**
 * El link vigente para mandar en el checkout, o null.
 * `edadMin` son los minutos desde el último click en el link (misma idea que adparams.js).
 */
export function getRef() {
  try {
    const obj = leer();
    if (!obj) return null;
    return { code: obj.ref, edadMin: Math.max(0, Math.round((Date.now() - obj.t) / 60000)) };
  } catch {
    return null;
  }
}
